import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Package, Users, FileText, CreditCard, Search, Filter, Save, X, AlertTriangle, Loader, AlertCircle, MapPin, Phone, Mail, Calendar } from 'lucide-react';

// Stock Management System Component
// 
// To integrate with your APIs:
// 1. Update the import paths below to match your project structure
// 2. Ensure your API interceptor is properly configured
// 3. The component automatically handles the response structure from your APIs
//
// Import actual API functions
import { 
  getAllSuppliers, createSupplier, updateSupplier, deleteSupplier,
  fetchActiveSuppliers
} from '../../../services/apiSupplier';

import { 
  getAllProducts, createProduct, updateProduct, updateProductStock, deleteProduct,
  fetchActiveProducts, getLowStockProducts
} from '../../../services/apiProduct';

import { 
  fetchAllBills, createBill, updateBill, deleteBill,
  fetchAllPayments, createPayment, addPaymentToBill
} from '../../../services/api';

const StockManagement = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);

  // Form states
  const [supplierForm, setSupplierForm] = useState({
    name: '', contactPerson: '', email: '', phone: '', address: '', taxId: '', paymentTerms: '30days'
  });
  const [productForm, setProductForm] = useState({
    name: '', sku: '', description: '', supplier: '', unitOfMeasure: 'pieces', 
    costPrice: '', sellingPrice: '', currentStock: ''
  });
  const [billForm, setBillForm] = useState({
    date: new Date().toISOString().split('T')[0], clientName: '', additional: []
  });
  const [paymentForm, setPaymentForm] = useState({
    billId: '', paymentMethod: 'cash', amount: '', paymentType: 'cr', notes: '', 
    paymentDate: new Date().toISOString().split('T')[0], addToBill: false
  });
  const [stockUpdateForm, setStockUpdateForm] = useState({
    productId: '', quantity: '', operation: 'add'
  });

  // Stats state
  const [stats, setStats] = useState({
    totalSuppliers: 0,
    totalProducts: 0,
    totalBills: 0,
    lowStockItems: 0,
    totalAmount: 0,
    activeSuppliers: 0
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'dashboard':
          await loadAllData();
          break;
        case 'suppliers':
          const suppliersResponse = await getAllSuppliers();
          setSuppliers(suppliersResponse.suppliers || suppliersResponse.data || []);
          break;
        case 'products':
          const productsResponse = await getAllProducts();
          setProducts(productsResponse.products || productsResponse.data || []);
          if (suppliers.length === 0) {
            const suppliersResponse = await getAllSuppliers();
            setSuppliers(suppliersResponse.suppliers || suppliersResponse.data || []);
          }
          break;
        case 'bills':
          const billsResponse = await fetchAllBills();
          setBills(billsResponse.bills || billsResponse.data || []);
          break;
        case 'payments':
          const paymentsResponse = await fetchAllPayments();
          setPayments(paymentsResponse.payments || paymentsResponse.data || []);
          break;
      }
      calculateStats();
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Error loading data: ' + error.message);
    }
    setLoading(false);
  };

  const loadAllData = async () => {
    try {
      const [suppliersResponse, productsResponse, billsResponse, paymentsResponse] = await Promise.all([
        getAllSuppliers(),
        getAllProducts(),
        fetchAllBills(),
        fetchAllPayments()
      ]);
      
      setSuppliers(suppliersResponse.suppliers || suppliersResponse.data || []);
      setProducts(productsResponse.products || productsResponse.data || []);
      setBills(billsResponse.bills || billsResponse.data || []);
      setPayments(paymentsResponse.payments || paymentsResponse.data || []);
    } catch (error) {
      throw error;
    }
  };

  const calculateStats = () => {
    const totalSuppliers = suppliers.length;
    const totalProducts = products.length;
    const totalBills = bills.length;
    const lowStockItems = products.filter(p => p.currentStock < 10).length;
    const totalAmount = bills.reduce((sum, bill) => sum + (bill.totalBillAmount || 0), 0);
    const activeSuppliers = suppliers.filter(s => s.isActive).length;

    setStats({
      totalSuppliers,
      totalProducts,
      totalBills,
      lowStockItems,
      totalAmount,
      activeSuppliers
    });
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setShowModal(true);
    
    if (item) {
      switch (type) {
        case 'supplier':
          setSupplierForm(item);
          break;
        case 'product':
          setProductForm({
            ...item,
            supplier: item.supplier?._id || item.supplier || ''
          });
          break;
        case 'bill':
          setBillForm({
            ...item,
            date: item.date ? new Date(item.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
          });
          break;
        case 'stock':
          setStockUpdateForm({ ...stockUpdateForm, productId: item._id });
          break;
        case 'payment':
          if (item.billId) {
            setPaymentForm({ ...paymentForm, billId: item._id });
          } else {
            setPaymentForm(item);
          }
          break;
      }
    } else {
      // Reset forms
      setSupplierForm({ name: '', contactPerson: '', email: '', phone: '', address: '', taxId: '', paymentTerms: '30days' });
      setProductForm({ name: '', sku: '', description: '', supplier: '', unitOfMeasure: 'pieces', costPrice: '', sellingPrice: '', currentStock: '' });
      setBillForm({ date: new Date().toISOString().split('T')[0], clientName: '', additional: [] });
      setPaymentForm({ billId: '', paymentMethod: 'cash', amount: '', paymentType: 'cr', notes: '', paymentDate: new Date().toISOString().split('T')[0], addToBill: false });
      setStockUpdateForm({ productId: '', quantity: '', operation: 'add' });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setEditingItem(null);
    setError(null);
  };

  const handleSubmit = async () => {
    setFormLoading(true);
    
    try {
      switch (modalType) {
        case 'supplier':
          if (editingItem) {
            await updateSupplier(editingItem._id, supplierForm);
            showNotification('Supplier updated successfully!');
          } else {
            await createSupplier(supplierForm);
            showNotification('Supplier created successfully!');
          }
          break;
        case 'product':
          if (editingItem) {
            await updateProduct(editingItem._id, productForm);
            showNotification('Product updated successfully!');
          } else {
            await createProduct(productForm);
            showNotification('Product created successfully!');
          }
          break;
        case 'bill':
          const billData = {
            ...billForm,
            drillFeet: 0,
            drillRate: 0,
            totalDrillAmount: 0,
            totalBillAmount: billForm.additional.reduce((sum, item) => sum + item.total, 0)
          };
          if (editingItem) {
            await updateBill(editingItem._id, billData);
            showNotification('Bill updated successfully!');
          } else {
            await createBill(billData);
            showNotification('Bill created successfully!');
          }
          break;
        case 'stock':
          await updateProductStock(stockUpdateForm.productId, {
            quantity: parseInt(stockUpdateForm.quantity),
            operation: stockUpdateForm.operation
          });
          showNotification('Stock updated successfully!');
          break;
        case 'payment':
          const paymentData = {
            billId: paymentForm.billId,
            paymentMethod: paymentForm.paymentMethod,
            amount: parseFloat(paymentForm.amount),
            paymentType: paymentForm.paymentType,
            notes: paymentForm.notes,
            paymentDate: paymentForm.paymentDate || new Date()
          };
          if (paymentForm.addToBill && paymentForm.billId) {
            await addPaymentToBill(paymentForm.billId, paymentData);
            showNotification('Payment added to bill successfully!');
          } else {
            await createPayment(paymentData);
            showNotification('Payment created successfully!');
          }
          break;
      }
      
      closeModal();
      loadData();
    } catch (error) {
      console.error('Error saving:', error);
      setError('Error saving data: ' + error.message);
    }
    setFormLoading(false);
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    setLoading(true);
    try {
      switch (type) {
        case 'supplier':
          await deleteSupplier(id);
          showNotification('Supplier deleted successfully!');
          break;
        case 'product':
          await deleteProduct(id);
          showNotification('Product deleted successfully!');
          break;
        case 'bill':
          await deleteBill(id);
          showNotification('Bill deleted successfully!');
          break;
      }
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Error deleting item: ' + error.message);
    }
    setLoading(false);
  };

  const addBillItem = () => {
    setBillForm({
      ...billForm,
      additional: [...billForm.additional, { name: '', quantity: 1, rate: 0, total: 0 }]
    });
  };

  const updateBillItem = (index, field, value) => {
    const newAdditional = [...billForm.additional];
    newAdditional[index][field] = value;
    
    if (field === 'quantity' || field === 'rate') {
      newAdditional[index].total = newAdditional[index].quantity * newAdditional[index].rate;
    }
    
    setBillForm({ ...billForm, additional: newAdditional });
  };

  const removeBillItem = (index) => {
    setBillForm({
      ...billForm,
      additional: billForm.additional.filter((_, i) => i !== index)
    });
  };

  const filteredData = () => {
    const data = activeTab === 'suppliers' ? suppliers : 
                 activeTab === 'products' ? products :
                 activeTab === 'bills' ? bills : payments;
    
    if (!searchTerm) return data;
    
    return data.filter(item => 
      Object.values(item).some(value => 
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className="p-3 rounded-full" style={{ backgroundColor: color + '20' }}>
          <Icon size={24} style={{ color }} />
        </div>
      </div>
    </div>
  );

  const SupplierCard = ({ supplier }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-3 rounded-full bg-blue-100">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{supplier.name}</h3>
            <p className="text-blue-600 font-medium">ID: {supplier._id}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => openModal('supplier', supplier)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit Supplier"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDelete(supplier._id, 'supplier')}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete Supplier"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center text-gray-600">
          <Users size={16} className="mr-2" />
          <span className="text-sm">{supplier.contactPerson}</span>
        </div>
        <div className="flex items-center text-gray-600">
          <Mail size={16} className="mr-2" />
          <span className="text-sm">{supplier.email}</span>
        </div>
        <div className="flex items-center text-gray-600">
          <Phone size={16} className="mr-2" />
          <span className="text-sm">{supplier.phone}</span>
        </div>
        <div className="flex items-start text-gray-600">
          <MapPin size={16} className="mr-2 mt-1 flex-shrink-0" />
          <span className="text-sm">{supplier.address}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
        <div>
          <p className="text-xs text-gray-500">Payment Terms</p>
          <p className="font-bold text-blue-600">{supplier.paymentTerms}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Status</p>
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            supplier.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {supplier.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    </div>
  );

  const ProductCard = ({ product }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-3 rounded-full ${product.currentStock < 10 ? 'bg-red-100' : 'bg-green-100'}`}>
            <Package className={`w-6 h-6 ${product.currentStock < 10 ? 'text-red-600' : 'text-green-600'}`} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{product.name}</h3>
            <p className="text-blue-600 font-medium">SKU: {product.sku}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => openModal('stock', product)}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Update Stock"
          >
            <Package size={16} />
          </button>
          <button
            onClick={() => openModal('product', product)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit Product"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDelete(product._id, 'product')}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete Product"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Current Stock</span>
          <div className="flex items-center">
            <span className={`font-bold ${product.currentStock < 10 ? 'text-red-600' : 'text-green-600'}`}>
              {product.currentStock} {product.unitOfMeasure}
            </span>
            {product.currentStock < 10 && <AlertTriangle className="w-4 h-4 text-red-500 ml-2" />}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Supplier</span>
          <span className="text-sm font-medium">
            {typeof product.supplier === 'object' ? product.supplier?.name : 
             suppliers.find(s => s._id === product.supplier)?.name || 'Unknown'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Cost Price</span>
          <span className="text-sm font-medium">{formatCurrency(product.costPrice)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Selling Price</span>
          <span className="text-sm font-bold text-green-600">{formatCurrency(product.sellingPrice)}</span>
        </div>
      </div>

      {product.description && (
        <div className="pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-600">{product.description}</p>
        </div>
      )}
    </div>
  );

  const BillCard = ({ bill }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-3 rounded-full bg-purple-100">
            <FileText className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{bill.clientName}</h3>
            <p className="text-purple-600 font-medium">Bill ID: {bill._id}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => openModal('bill', bill)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit Bill"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDelete(bill._id, 'bill')}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete Bill"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center text-gray-600">
          <Calendar size={16} className="mr-2" />
          <span className="text-sm">{new Date(bill.date).toLocaleDateString()}</span>
        </div>
        <div className="text-sm text-gray-600">
          <p className="font-medium mb-2">Items:</p>
          <div className="space-y-1">
            {bill.additional?.map((item, idx) => (
              <div key={idx} className="flex justify-between text-xs bg-gray-50 p-2 rounded">
                <span>{item.name} (Qty: {item.quantity})</span>
                <span>{formatCurrency(item.total)}</span>
              </div>
            ))}
            {(!bill.additional || bill.additional.length === 0) && (
              <span className="text-gray-400 italic">No items</span>
            )}
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-100">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Total Amount</span>
          <span className="text-lg font-bold text-green-600">{formatCurrency(bill.totalBillAmount)}</span>
        </div>
      </div>
    </div>
  );

  const PaymentCard = ({ payment }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-3 rounded-full ${payment.paymentType === 'cr' ? 'bg-green-100' : 'bg-red-100'}`}>
            <CreditCard className={`w-6 h-6 ${payment.paymentType === 'cr' ? 'text-green-600' : 'text-red-600'}`} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{formatCurrency(payment.amount)}</h3>
            <p className="text-gray-600 font-medium">Payment ID: {payment._id}</p>
          </div>
        </div>
        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
          payment.paymentType === 'cr' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {payment.paymentType === 'cr' ? 'Credit' : 'Debit'}
        </span>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center text-gray-600">
          <Calendar size={16} className="mr-2" />
          <span className="text-sm">{new Date(payment.paymentDate).toLocaleDateString()}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Method</span>
          <span className="text-sm font-medium capitalize">{payment.paymentMethod}</span>
        </div>
        {payment.billId && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Bill ID</span>
            <span className="text-sm font-medium">{payment.billId}</span>
          </div>
        )}
        {payment.notes && (
          <div className="pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-600">{payment.notes}</p>
          </div>
        )}
      </div>
    </div>
  );

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-12">
      <Loader className="animate-spin text-blue-600" size={32} />
      <span className="ml-2 text-gray-600">Loading...</span>
    </div>
  );

  const ErrorMessage = ({ message, onRetry }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
      <AlertCircle className="text-red-600" size={20} />
      <div className="flex-1">
        <p className="text-red-800 font-medium">Error</p>
        <p className="text-red-600 text-sm">{message}</p>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  );

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Package },
    { id: 'suppliers', label: 'Suppliers', icon: Users },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'bills', label: 'Bills', icon: FileText },
    { id: 'payments', label: 'Payments', icon: CreditCard }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg ${
          notification.type === 'error' ? 'bg-red-100 border border-red-400 text-red-700' : 'bg-green-100 border border-green-400 text-green-700'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-2xl font-bold text-blue-900">📦 Stock Management System</div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={loadData}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                {loading ? <Loader size={16} className="animate-spin" /> : '🔄 Refresh'}
              </button>
              <div className="text-sm text-gray-600">
                {new Date().toLocaleDateString('en-IN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="mx-auto px-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon size={18} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="mx-auto px-6 py-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} onRetry={loadData} />
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-900">Stock Dashboard</h1>
              <button
                onClick={() => openModal('supplier')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
                disabled={loading}
              >
                <Plus size={20} />
                <span>Quick Add</span>
              </button>
            </div>

            {loading && <LoadingSpinner />}

            {!loading && (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard
                    title="Total Suppliers"
                    value={stats.totalSuppliers}
                    icon={Users}
                    color="#3B82F6"
                    subtitle={`${stats.activeSuppliers} active`}
                  />
                  <StatCard
                    title="Total Products"
                    value={stats.totalProducts}
                    icon={Package}
                    color="#10B981"
                    subtitle="In inventory"
                  />
                  <StatCard
                    title="Low Stock Items"
                    value={stats.lowStockItems}
                    icon={AlertTriangle}
                    color="#F59E0B"
                    subtitle="Need attention"
                  />
                  <StatCard
                    title="Total Bills Value"
                    value={formatCurrency(stats.totalAmount)}
                    icon={FileText}
                    color="#8B5CF6"
                    subtitle={`${stats.totalBills} bills`}
                  />
                </div>

                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Low Stock Products */}
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">Low Stock Alert</h2>
                    <div className="space-y-4">
                      {products
                        .filter(p => p.currentStock < 10)
                        .slice(0, 5)
                        .map((product) => (
                          <div key={product._id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <AlertTriangle className="w-5 h-5 text-red-600" />
                              <div>
                                <p className="font-medium text-gray-900">{product.name}</p>
                                <p className="text-sm text-gray-600">SKU: {product.sku}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-red-600">{product.currentStock}</p>
                              <p className="text-xs text-gray-500">{product.unitOfMeasure}</p>
                            </div>
                          </div>
                        ))}
                      {products.filter(p => p.currentStock < 10).length === 0 && (
                        <p className="text-gray-500 text-center py-4">All products are well stocked! 🎉</p>
                      )}
                    </div>
                  </div>

                  {/* Top Suppliers */}
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">Top Suppliers</h2>
                    <div className="space-y-4">
                      {suppliers
                        .filter(s => s.isActive)
                        .slice(0, 5)
                        .map((supplier, index) => (
                          <div key={supplier._id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                            <div className="p-2 rounded-full bg-blue-100">
                              <Users className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{supplier.name}</p>
                              <p className="text-sm text-gray-600">{supplier.contactPerson}</p>
                            </div>
                            <span className="text-xs text-gray-500">{supplier.paymentTerms}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Suppliers Tab */}
        {activeTab === 'suppliers' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-900">Suppliers</h1>
              <button
                onClick={() => openModal('supplier')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
                disabled={loading}
              >
                <Plus size={20} />
                <span>Add Supplier</span>
              </button>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search suppliers by name, contact person, or email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredData().map(supplier => (
                  <SupplierCard key={supplier._id} supplier={supplier} />
                ))}
              </div>
            )}

            {filteredData().length === 0 && !loading && (
              <div className="text-center py-12">
                <Users size={48} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No suppliers found</h3>
                <p className="text-gray-500">Try adjusting your search criteria</p>
              </div>
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-900">Products & Stock</h1>
              <div className="flex space-x-2">
                <button
                  onClick={() => openModal('stock')}
                  className="border border-green-300 text-green-700 px-4 py-2 rounded-lg hover:bg-green-50 flex items-center space-x-2 transition-colors"
                >
                  <Package size={20} />
                  <span>Update Stock</span>
                </button>
                <button
                  onClick={() => openModal('product')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
                  disabled={loading}
                >
                  <Plus size={20} />
                  <span>Add Product</span>
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search products by name, SKU, or supplier..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredData().map(product => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </div>
            )}

            {filteredData().length === 0 && !loading && (
              <div className="text-center py-12">
                <Package size={48} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500">Try adjusting your search criteria</p>
              </div>
            )}
          </div>
        )}

        {/* Bills Tab */}
        {activeTab === 'bills' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-900">Bills</h1>
              <div className="flex space-x-2">
                <button
                  onClick={() => openModal('payment')}
                  className="border border-purple-300 text-purple-700 px-4 py-2 rounded-lg hover:bg-purple-50 flex items-center space-x-2 transition-colors"
                >
                  <CreditCard size={20} />
                  <span>Add Payment</span>
                </button>
                <button
                  onClick={() => openModal('bill')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
                  disabled={loading}
                >
                  <Plus size={20} />
                  <span>Create Bill</span>
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search bills by client name or bill ID..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredData().map(bill => (
                  <BillCard key={bill._id} bill={bill} />
                ))}
              </div>
            )}

            {filteredData().length === 0 && !loading && (
              <div className="text-center py-12">
                <FileText size={48} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No bills found</h3>
                <p className="text-gray-500">Try adjusting your search criteria</p>
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
              <button
                onClick={() => openModal('payment')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
                disabled={loading}
              >
                <Plus size={20} />
                <span>Add Payment</span>
              </button>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search payments by bill ID or payment method..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredData().map(payment => (
                  <PaymentCard key={payment._id} payment={payment} />
                ))}
              </div>
            )}

            {filteredData().length === 0 && !loading && (
              <div className="text-center py-12">
                <CreditCard size={48} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
                <p className="text-gray-500">Try adjusting your search criteria</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Forms */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingItem ? 'Edit' : 'Add'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
                </h2>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" disabled={formLoading}>
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Supplier Form */}
                {modalType === 'supplier' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        required
                        value={supplierForm.name}
                        onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person *</label>
                      <input
                        type="text"
                        required
                        value={supplierForm.contactPerson}
                        onChange={(e) => setSupplierForm({ ...supplierForm, contactPerson: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        value={supplierForm.email}
                        onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                      <input
                        type="tel"
                        required
                        value={supplierForm.phone}
                        onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <textarea
                        value={supplierForm.address}
                        onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="3"
                        disabled={formLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                      <input
                        type="text"
                        value={supplierForm.taxId}
                        onChange={(e) => setSupplierForm({ ...supplierForm, taxId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                      <select
                        value={supplierForm.paymentTerms}
                        onChange={(e) => setSupplierForm({ ...supplierForm, paymentTerms: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      >
                        <option value="immediate">Immediate</option>
                        <option value="15days">15 Days</option>
                        <option value="30days">30 Days</option>
                        <option value="45days">45 Days</option>
                        <option value="60days">60 Days</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Product Form */}
                {modalType === 'product' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        required
                        value={productForm.name}
                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
                      <input
                        type="text"
                        required
                        value={productForm.sku}
                        onChange={(e) => setProductForm({ ...productForm, sku: e.target.value.toUpperCase() })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="3"
                        disabled={formLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                      <select
                        required
                        value={productForm.supplier}
                        onChange={(e) => setProductForm({ ...productForm, supplier: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      >
                        <option value="">Select Supplier</option>
                        {suppliers.map(supplier => (
                          <option key={supplier._id} value={supplier._id}>{supplier.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure</label>
                      <select
                        value={productForm.unitOfMeasure}
                        onChange={(e) => setProductForm({ ...productForm, unitOfMeasure: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      >
                        <option value="pieces">Pieces</option>
                        <option value="kg">Kg</option>
                        <option value="grams">Grams</option>
                        <option value="liters">Liters</option>
                        <option value="meters">Meters</option>
                        <option value="boxes">Boxes</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price *</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        value={productForm.costPrice}
                        onChange={(e) => setProductForm({ ...productForm, costPrice: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price *</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        value={productForm.sellingPrice}
                        onChange={(e) => setProductForm({ ...productForm, sellingPrice: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Stock</label>
                      <input
                        type="number"
                        min="0"
                        value={productForm.currentStock}
                        onChange={(e) => setProductForm({ ...productForm, currentStock: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                  </div>
                )}

                {/* Bill Form */}
                {modalType === 'bill' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                        <input
                          type="date"
                          required
                          value={billForm.date}
                          onChange={(e) => setBillForm({ ...billForm, date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={formLoading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                        <input
                          type="text"
                          required
                          value={billForm.clientName}
                          onChange={(e) => setBillForm({ ...billForm, clientName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={formLoading}
                        />
                      </div>
                    </div>

                    {/* Bill Items */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <label className="block text-sm font-medium text-gray-700">Items</label>
                        <button
                          type="button"
                          onClick={addBillItem}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                          disabled={formLoading}
                        >
                          Add Item
                        </button>
                      </div>

                      <div className="space-y-3">
                        {billForm.additional.map((item, index) => (
                          <div key={index} className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-4">
                              <label className="block text-xs text-gray-500 mb-1">Item Name</label>
                              <select
                                value={item.name}
                                onChange={(e) => {
                                  const selectedProduct = products.find(p => p.name === e.target.value);
                                  if (selectedProduct) {
                                    updateBillItem(index, 'name', selectedProduct.name);
                                    updateBillItem(index, 'rate', selectedProduct.sellingPrice);
                                  } else {
                                    updateBillItem(index, 'name', e.target.value);
                                  }
                                }}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                                disabled={formLoading}
                              >
                                <option value="">Select Product</option>
                                {products.map(product => (
                                  <option key={product._id} value={product.name}>
                                    {product.name} (₹{product.sellingPrice})
                                  </option>
                                ))}
                              </select>
                              {!products.find(p => p.name === item.name) && (
                                <input
                                  type="text"
                                  placeholder="Custom item"
                                  value={item.name}
                                  onChange={(e) => updateBillItem(index, 'name', e.target.value)}
                                  className="mt-1 w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                                  disabled={formLoading}
                                />
                              )}
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">Qty</label>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateBillItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                                disabled={formLoading}
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">Rate</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.rate}
                                onChange={(e) => updateBillItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                                disabled={formLoading}
                              />
                            </div>
                            <div className="col-span-3">
                              <label className="block text-xs text-gray-500 mb-1">Total</label>
                              <input
                                type="text"
                                value={formatCurrency(item.total)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50"
                                disabled
                              />
                            </div>
                            <div className="col-span-1">
                              {billForm.additional.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeBillItem(index)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  disabled={formLoading}
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700">Total Amount:</span>
                          <span className="text-lg font-bold text-green-600">
                            {formatCurrency(billForm.additional.reduce((sum, item) => sum + item.total, 0))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stock Update Form */}
                {modalType === 'stock' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                      <select
                        required
                        value={stockUpdateForm.productId}
                        onChange={(e) => setStockUpdateForm({ ...stockUpdateForm, productId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      >
                        <option value="">Select Product</option>
                        {products.map(product => (
                          <option key={product._id} value={product._id}>
                            {product.name} ({product.sku}) - Current: {product.currentStock}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Operation</label>
                      <select
                        value={stockUpdateForm.operation}
                        onChange={(e) => setStockUpdateForm({ ...stockUpdateForm, operation: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      >
                        <option value="add">Add Stock</option>
                        <option value="subtract">Subtract Stock</option>
                        <option value="set">Set Stock</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                      <input
                        type="number"
                        min="0"
                        required
                        value={stockUpdateForm.quantity}
                        onChange={(e) => setStockUpdateForm({ ...stockUpdateForm, quantity: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                  </div>
                )}

                {/* Payment Form */}
                {modalType === 'payment' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bill (Optional)</label>
                      <select
                        value={paymentForm.billId}
                        onChange={(e) => setPaymentForm({ ...paymentForm, billId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      >
                        <option value="">Select Bill</option>
                        {bills.map(bill => (
                          <option key={bill._id} value={bill._id}>
                            {bill.clientName} - {formatCurrency(bill.totalBillAmount)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                      <select
                        required
                        value={paymentForm.paymentMethod}
                        onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      >
                        <option value="cash">Cash</option>
                        <option value="cheque">Cheque</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="upi">UPI</option>
                        <option value="card">Card</option>
                        <option value="online">Online</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                      <select
                        value={paymentForm.paymentType}
                        onChange={(e) => setPaymentForm({ ...paymentForm, paymentType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      >
                        <option value="cr">Credit (Received)</option>
                        <option value="dr">Debit (Paid)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                      <input
                        type="date"
                        value={paymentForm.paymentDate}
                        onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={formLoading}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={paymentForm.notes}
                        onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                        maxLength="500"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="3"
                        placeholder="Additional notes (max 500 characters)"
                        disabled={formLoading}
                      />
                    </div>
                    {paymentForm.billId && (
                      <div className="md:col-span-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={paymentForm.addToBill}
                            onChange={(e) => setPaymentForm({ ...paymentForm, addToBill: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200"
                            disabled={formLoading}
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Add this payment directly to the selected bill
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    disabled={formLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                    disabled={formLoading}
                  >
                    {formLoading ? (
                      <Loader size={16} className="animate-spin" />
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {editingItem ? 'Update' : 'Save'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockManagement;